/**
 * AdminAuthContext — single source of truth for the current admin's session.
 *
 * On mount it tries `/admin/me` with the stored token. While loading we render
 * children gated by `loading=true`. Use `useAdmin()` to read.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { adminLogin, adminLogout, adminMe, clearAdminToken, getAdminToken, setAdminToken } from "../lib/adminApi";
import { clearUserToken, getUserToken, setUserToken } from "../lib/userApi";
import { resetIdentity } from "../lib/analytics";

const AdminAuthContext = createContext({
  admin: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AdminAuthProvider({ children }) {
  // Synchronous fast-path: no admin token OR no user token → we KNOW this
  // session has no admin privileges. Skip /admin/me entirely so no
  // "جاري التحقق…" flicker shows on a fresh visit.
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(
    () => Boolean(getAdminToken() && getUserToken())
  );

  const refresh = useCallback(async () => {
    try {
      // Security invariant: an admin session must ALWAYS be paired with a
      // user session (admin login issues both tokens together). If either
      // token is missing, both must be cleared so a stale admin_token from a
      // previous session can NEVER expose admin UI to a logged-out visitor.
      const aTok = getAdminToken();
      const uTok = getUserToken();
      if (!aTok || !uTok) {
        clearAdminToken();
        setAdmin(null);
        setLoading(false);
        return;
      }
      const me = await adminMe();
      setAdmin(me);
    } catch (e) {
      // 401 / network failure → drop the admin token defensively.
      clearAdminToken();
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (username, password) => {
    const data = await adminLogin(username, password);
    setAdminToken(data.token);
    // Admin login also issues a paired user token so the admin can use lawyer features
    if (data.user_token) setUserToken(data.user_token);
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(async () => {
    await adminLogout();
    clearAdminToken();
    clearUserToken();
    setAdmin(null);
    // أَلغِ رِبط هُويّة PostHog حَتّى لا تَنزَلِق أَحداث الزّائر التّالي إلى المُستَخدِم السّابِق.
    resetIdentity();
    // Force a full reload so UserAuthContext refreshes its state
    window.location.href = "/admin/login";
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminAuthContext);
